import CoffeeFlatControl from '../Common/CoffeeFlatControl';
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { AuthContext, SuccessNotification, BalanceWarningModal } from '../../App';
import { 
  BreakfastSummaryTable, 
  StatisticsTab,
  UnifiedMenuManagementTab,
  BreakfastHistoryTab,
  OtherDepartmentsTab,
  AdminSettingsTab,
  CoffeeAndEggsManagement,
  FlexiblePaymentModal,
  ExtendedOrderHistoryTab
} from './AdminComponents';
import { NewEmployeeModal } from '../Employee/EmployeeMenu';
import { getBerlinDate } from '../../utils/berlinDate';
import DashboardSidebar from '../Common/DashboardSidebar';
import { useStationMode } from '../Common/useStationMode';
import ScrollToTop from '../Common/ScrollToTop';
import AnnouncementBanner from '../Common/AnnouncementBanner';
import EmployeeHistoryModal from '../Employee/EmployeeHistoryModal';
import DepartmentOnboarding from './Onboarding/DepartmentOnboarding';
import { shouldShowDepartmentOnboarding } from './Onboarding/onboardingState';
import { getAdminEmployeeSearchResults } from './adminEmployeeSearch';
import EmployeeCategoryManager from './EmployeeCategoryManager';
import { saveCategoryOrder } from './categoryOrdering';
import {
  buildNonEmptyEmployeeDisplaySections,
  isRegularDepartmentEmployee,
} from './employeeCategoryDisplay';
import {
  persistEmployeeOrder,
  reorderEmployeeWithinCategory,
} from './employeeOrdering';
import PaymentDirectionSelector from './PaymentDirectionSelector';
import {
  PAYMENT_DIRECTION_DEPOSIT,
  signedPaymentAmount,
} from './paymentDirection';
import PaymentStatusNotice from '../../features/payment/PaymentStatusNotice';
import PaymentDetails from '../../features/payment/PaymentDetails';
import BalanceDeadlineSettings from './BalanceDeadlineSettings';
import RecipeLibrary from '../Organization/RecipeLibrary';
import CategoryImportDialog from './CategoryImportDialog';
import { createLatestDepartmentConfigLoader } from './departmentConfigLoader';
import { Users, ShoppingCart, BookOpen, BarChart3, Settings, Calendar, FileText, Building2, Coffee, LifeBuoy, CreditCard } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export const ADMIN_MENU_ITEMS = [
  { id: 'employees', label: 'Mitarbeiter', icon: Users },
  { id: 'statistics', label: 'Statistik', icon: BarChart3 },
  { id: 'other-departments', label: 'Andere WA', icon: Building2 },
  { id: 'menu-management', label: 'Menü & Preise', icon: ShoppingCart },
  { id: 'recipes', label: 'Rezepte verwalten', icon: BookOpen },
  { id: 'breakfast-history', label: 'Essensverlauf', icon: Calendar },
  { id: 'extended-history', label: 'Erweiterter Verlauf', icon: FileText },
  { id: 'settings', label: 'Einstellungen', icon: Settings },
  { id: 'balance-deadlines', label: 'Saldo & Zahlungsfristen', icon: CreditCard, isSubItem: true },
  { id: 'help', label: 'Hilfe & Neues', icon: FileText, isSubItem: true },
  { id: 'support', label: 'Support', icon: LifeBuoy, isSubItem: true, href: 'https://helpdesk.creativeymedia.de/help/fw-kantine' },
  { id: 'payment', label: 'Zahlung', icon: CreditCard, isSubItem: true },
  { id: 'referral', label: 'Werben', icon: Users, isSubItem: true }
];

export const getAdminMenuItems = (isSingleDepartment = false) => (
  ADMIN_MENU_ITEMS.map(item => isSingleDepartment && item.id === 'other-departments' ? {...item, label:'Andere Wachen'} : item)
);

export const getInitialAdminTab = (savedTab, isSingleDepartment = false) => (
  getAdminMenuItems(isSingleDepartment).some(item => item.id === savedTab) ? savedTab : 'employees'
);

const DepartmentAdminDashboard = () => {
  const { isSingleDepartment, loading: stationModeLoading } = useStationMode();
  // Load active tab from localStorage or default to 'employees'
  const [activeTab, setActiveTab] = useState(() => {
    return getInitialAdminTab(localStorage.getItem('departmentAdminDashboard_activeTab'), isSingleDepartment);
  });
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [eightHourEmployees, setEightHourEmployees] = useState([]); // NEU: 8H-Dienst Mitarbeiter
  const [statisticsCategories, setStatisticsCategories] = useState([]);
  const [breakfastMenu, setBreakfastMenu] = useState([]);
  const [toppingsMenu, setToppingsMenu] = useState([]);
  const [drinksMenu, setDrinksMenu] = useState([]);
  const [sweetsMenu, setSweetsMenu] = useState([]);
  const [menuCategories, setMenuCategories] = useState([]);
  const [onboarding, setOnboarding] = useState(null);
  const [onboardingPaused, setOnboardingPaused] = useState(false);
  const [tutorialReplay, setTutorialReplay] = useState(false);
  const [showNewEmployee, setShowNewEmployee] = useState(false);
  const [showNewDrink, setShowNewDrink] = useState(false);
  const [showNewSweet, setShowNewSweet] = useState(false);
  
  // Sponsoring state
  const [sponsorStatus, setSponsorStatus] = useState({
    breakfast_sponsored: null,
    lunch_sponsored: null
  });
  
  // NEW: Flexible Payment Modal State  
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentEmployeeData, setPaymentEmployeeData] = useState(null);
  
  // Success Notification State
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // NEW: Payment Status State
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentStatusLoading, setPaymentStatusLoading] = useState(true);
  const [paymentStatusError, setPaymentStatusError] = useState(false);
  const [paymentDetailsExpanded, setPaymentDetailsExpanded] = useState(false);
  
  // Balance Warning Modal State
  const [showBalanceWarning, setShowBalanceWarning] = useState(false);
  const [balanceWarningData, setBalanceWarningData] = useState({ employeeName: '', openBalances: [] });
  
  // NEU: Department configuration
  const [departmentConfig, setDepartmentConfig] = useState({
    use_combined_accounts: false,
    is_breakfast_lunch_enabled: true,
    is_drinks_enabled: true,
    is_snacks_enabled: true,
    is_shopping_list_enabled: false,
    is_recipe_library_enabled: false,
    available_roll_types: ['weiss', 'koerner'],
    breakfast_item_visibility: {
      boiled_eggs: true,
      fried_eggs: true,
      scrambled_eggs: true,
      coffee: true,
    },
    default_lunch_price: null,
    default_dinner_price: null,
    meal_types: {
      breakfast: { enabled: true, locked_today: false },
      lunch: { enabled: true, locked_today: false },
      dinner: { enabled: true, locked_today: false },
      drinks: { enabled: true, locked_today: false },
      snacks: { enabled: true, locked_today: false },
    },
  });
  const [departmentConfigLoaded, setDepartmentConfigLoaded] = useState(false);
  const departmentConfigLoaderRef = useRef(null);
  if (!departmentConfigLoaderRef.current) {
    departmentConfigLoaderRef.current = createLatestDepartmentConfigLoader();
  }
  
  // NEU: Extended Order History State
  const [extendedOrderHistory, setExtendedOrderHistory] = useState([]);
  const [extendedOrderHistoryPagination, setExtendedOrderHistoryPagination] = useState({
    page: 1,
    page_size: 30,
    total: 0,
    total_pages: 1,
  });
  
  // NEU: Help Content State
  const [globalHelpContent, setGlobalHelpContent] = useState('');
  const [globalHelpLastUpdated, setGlobalHelpLastUpdated] = useState(null);
  
  // NEU: Egg Names State
  const [eggNames, setEggNames] = useState({
    boiled: 'Gekochte Eier',
    fried: 'Spiegeleier',
    scrambled: 'Rührei'
  });
  
  const { currentDepartment, logout, loginDepartment } = React.useContext(AuthContext);

  // Helper function to get department name from ID
  const getDepartmentName = (departmentId) => {
    // DYNAMICALLY extracts department number from ID (e.g., fw4abteilung1 OR fw9100abteilung1 → "Wa. 1.")
    // Supports multi-tenant setups with any wache_nummer
    const match = departmentId.match(/abteilung(\d+)$/);
    if (match) {
      return `Wa. ${match[1]}.`;
    }
    return departmentId; // Fallback to original ID if pattern doesn't match
  };

  // Persist active tab to localStorage
  useEffect(() => {
    localStorage.setItem('departmentAdminDashboard_activeTab', activeTab);
  }, [activeTab]);

  const goBackToEmployeeDashboard = () => {
    // Use the loginDepartment function to properly save employee mode in localStorage
    const departmentData = {
      department_id: currentDepartment.department_id,
      department_name: currentDepartment.department_name,
      role: 'employee'
    };
    
    // Use the already imported loginDepartment function
    loginDepartment(departmentData);
  };

  useEffect(() => {
    if (currentDepartment) {
      const departmentId = currentDepartment.department_id;
      fetchEmployees();
      fetch8HourEmployees(); // NEU: Lade 8H-Mitarbeiter
      fetchStatisticsCategories();
      fetchMenus();
      fetchEggNames(); // NEU: Lade custom Eiernamen
      fetchDepartmentConfig(departmentId);
      fetchOnboarding();
      setOnboardingPaused(false);
    }
    return () => departmentConfigLoaderRef.current.invalidate();
  }, [currentDepartment]);

  const fetchStatisticsCategories = async () => {
    if (!currentDepartment?.department_id) return;
    try {
      const response = await axios.get(`${API}/departments/${currentDepartment.department_id}/categories`);
      setStatisticsCategories(response.data || []);
    } catch (error) {
      console.error('Error loading employee categories for statistics:', error);
      setStatisticsCategories([]);
    }
  };

  const fetchOnboarding = async () => {
    if (!currentDepartment?.department_id) return;
    try {
      const response = await axios.get(
        `${API}/department-admin/onboarding/${currentDepartment.department_id}`
      );
      setOnboarding(response.data);
    } catch (error) {
      console.error('Fehler beim Laden des Onboardings:', error);
      // A failed onboarding request must never lock admins out of the dashboard.
      setOnboarding(null);
    }
  };

  const handleOnboardingCompleted = async () => {
    setOnboardingPaused(false);
    await Promise.all([
      fetchOnboarding(),
      fetchEmployees(),
      fetchMenus(),
      fetchDepartmentConfig(),
    ]);
  };

  useEffect(() => {
    if (activeTab === 'statistics' && currentDepartment) {
      fetchStatisticsCategories();
    }
  }, [activeTab, currentDepartment]);

  // NEW: Load Payment Status
  const fetchPaymentStatus = async () => {
    try {
      setPaymentStatusLoading(true);
      setPaymentStatusError(false);
      const response = await axios.get(`${API}/payment/status-for-department/${currentDepartment.department_id}`);
      
      if (response.data.found) {
        setPaymentStatus(response.data.status);
      } else {
        setPaymentStatus(null);
      }
    } catch (error) {
      console.error('Error loading payment status:', error);
      setPaymentStatusError(true);
      // Keep the last successful Payment snapshot during a temporary outage.
    } finally {
      setPaymentStatusLoading(false);
    }
  };
  
  useEffect(() => {
    if (!currentDepartment?.department_id) return undefined;
    fetchPaymentStatus();
    const interval = window.setInterval(fetchPaymentStatus, 60 * 60 * 1000);
    return () => window.clearInterval(interval);
  }, [currentDepartment?.department_id]);

  useEffect(() => {
    if (activeTab === 'payment' && currentDepartment?.department_id) fetchPaymentStatus();
  }, [activeTab]);


  // NEU: Load data when switching to specific tabs
  useEffect(() => {
    if (activeTab === 'extended-history' && currentDepartment) {
      fetchExtendedOrderHistory();
    } else if (activeTab === 'help') {
      fetchHelpContent();
    }
  }, [activeTab, currentDepartment]);


  const fetchDepartmentConfig = async (
    departmentId = currentDepartment?.department_id
  ) => {
    if (!departmentId) return;
    setDepartmentConfigLoaded(false);
    setDepartmentConfig(current => ({
      ...current,
      is_shopping_list_enabled: false,
      is_recipe_library_enabled: false,
    }));
    try {
        const response = await departmentConfigLoaderRef.current.load(
          departmentId,
          currentId => axios.get(`${API}/department-config/${currentId}`)
        );
        if (!response) return;
        setDepartmentConfig({
          use_combined_accounts: response.data.use_combined_accounts,
          is_breakfast_lunch_enabled: response.data.is_breakfast_lunch_enabled,
          is_drinks_enabled: response.data.is_drinks_enabled,
          is_snacks_enabled: response.data.is_snacks_enabled,
          is_shopping_list_enabled: response.data.is_shopping_list_enabled === true,
          is_recipe_library_enabled: response.data.is_recipe_library_enabled === true,
          available_roll_types: response.data.available_roll_types || ['weiss', 'koerner'],
          breakfast_item_visibility: response.data.breakfast_item_visibility || {
            boiled_eggs: true,
            fried_eggs: true,
            scrambled_eggs: true,
            coffee: true,
          },
          default_lunch_price: response.data.default_lunch_price || null,
          default_dinner_price: response.data.default_dinner_price || null,
          meal_types: response.data.meal_types || null,
        });
        setDepartmentConfigLoaded(true);
    } catch (error) {
      console.error('Fehler beim Laden der Department-Konfiguration:', error);
    }
  };

  const updateRollTypes = async (rollTypes) => {
    try {
      await axios.put(`${API}/department-admin/roll-types/${currentDepartment.department_id}`, rollTypes);
      setDepartmentConfig(prev => ({ ...prev, available_roll_types: rollTypes }));
    } catch (error) {
      alert(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    }
  };

  const updateOrganizationFeatures = async (nextSettings) => {
    const response = await axios.put(
      `${API}/department-admin/${currentDepartment.department_id}/organization-features`,
      nextSettings
    );
    setDepartmentConfig(current => ({ ...current, ...response.data }));
  };

  const updateBreakfastItemVisibility = async (visibility) => {
    try {
      const response = await axios.put(
        `${API}/department-admin/breakfast-item-visibility/${currentDepartment.department_id}`,
        visibility
      );
      setDepartmentConfig(prev => ({
        ...prev,
        breakfast_item_visibility: response.data.breakfast_item_visibility || visibility
      }));
    } catch (error) {
      alert(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    }
  };

  const updateDefaultLunchPrice = async (price) => {
    try {
      await axios.put(`${API}/department-admin/default-lunch-price/${currentDepartment.department_id}?price=${price}`);
      setDepartmentConfig(prev => ({ ...prev, default_lunch_price: price > 0 ? price : null }));
    } catch (error) {
      alert(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    }
  };

  const updateDefaultDinnerPrice = async (price) => {
    try {
      await axios.put(`${API}/department-settings/${currentDepartment.department_id}/default-dinner-price?price=${price}`);
      setDepartmentConfig(prev => ({ ...prev, default_dinner_price: price > 0 ? price : null }));
    } catch (error) {
      alert(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    }
  };

  const toggleDinnerEnabled = async (enabled) => {
    try {
      const res = await axios.put(`${API}/department-settings/${currentDepartment.department_id}/meal-toggle?meal_type=dinner&enabled=${enabled}`);
      setDepartmentConfig(prev => ({ ...prev, meal_types: res.data.meal_types }));
    } catch (error) {
      alert(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    }
  };

  // Auto-refresh employee data when switching to employees tab and scroll to top
  useEffect(() => {
    if ((activeTab === 'employees' || activeTab === 'statistics') && currentDepartment) {
      fetchEmployees();
      fetch8HourEmployees(); // NEU: Reload 8H employees when switching to employees/statistics tab
    }
    // Scroll to top when changing tabs
    window.scrollTo(0, 0);
  }, [activeTab, currentDepartment]);

  const fetch8HourEmployees = async () => {
    try {
      const response = await axios.get(`${API}/departments/${currentDepartment.department_id}/8h-employees`);
      setEightHourEmployees(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der 8H-Mitarbeiter:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(
        `${API}/departments/${currentDepartment.department_id}/employees`
      );
      setEmployees(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Mitarbeiter:', error);
    }
  };

  const handleBalanceUpdated = () => {
    // Refresh the employee list after balance update in Admin Dashboard
    fetchEmployees();
  };

  const fetchMenus = async () => {
    try {
      if (!currentDepartment?.department_id) {
        console.error('No department ID available for admin menu fetch');
        return;
      }

      const departmentId = currentDepartment.department_id;
      const [breakfast, toppings, drinks, sweets, categories] = await Promise.all([
        axios.get(`${API}/menu/breakfast/${departmentId}`),
        axios.get(`${API}/menu/toppings/${departmentId}`),
        axios.get(`${API}/menu/drinks/${departmentId}`),
        axios.get(`${API}/menu/sweets/${departmentId}`),
        axios.get(`${API}/menu/categories/${departmentId}?include_archived=true`)
      ]);
      setBreakfastMenu(breakfast.data);
      setToppingsMenu(toppings.data);
      setDrinksMenu(drinks.data);
      setSweetsMenu(sweets.data);
      setMenuCategories(categories.data);
    } catch (error) {
      console.error('Fehler beim Laden der Menüs:', error);
      // Fallback to old endpoints if department-specific ones fail
      try {
        const [breakfast, toppings, drinks, sweets] = await Promise.all([
          axios.get(`${API}/menu/breakfast`),
          axios.get(`${API}/menu/toppings`),
          axios.get(`${API}/menu/drinks`),
          axios.get(`${API}/menu/sweets`)
        ]);
        setBreakfastMenu(breakfast.data);
        setToppingsMenu(toppings.data);
        setDrinksMenu(drinks.data);
        setSweetsMenu(sweets.data);
        setMenuCategories([
          { id: `${currentDepartment.department_id}:drinks`, system_key: 'drinks', name: 'Getränke', is_system: true, products: drinks.data },
          { id: `${currentDepartment.department_id}:snacks`, system_key: 'snacks', name: 'Snacks', is_system: true, products: sweets.data },
        ]);
      } catch (fallbackError) {
        console.error('Fallback admin menu loading also failed:', fallbackError);
      }
    }
  };

  const fetchEggNames = async () => {
    try {
      if (!currentDepartment?.department_id) {
        return;
      }
      const response = await axios.get(`${API}/department-settings/${currentDepartment.department_id}`);
      setEggNames({
        boiled: response.data.boiled_eggs_name || 'Gekochte Eier',
        fried: response.data.fried_eggs_name || 'Spiegeleier',
        scrambled: response.data.scrambled_eggs_name || 'Rührei'
      });
    } catch (error) {
      console.error('Fehler beim Laden der Eiernamen:', error);
    }
  };

  const handleCreateEmployee = async (name, isGuest = false, is8HService = false, categoryId = null) => {
    try {
      const response = await axios.post(`${API}/employees`, {
        name,
        department_id: currentDepartment.department_id,
        is_guest: isGuest,
        is_8h_service: is8HService
      });
      if (categoryId) {
        await axios.put(`${API}/department-admin/${currentDepartment.department_id}/employees/${response.data.id}/category?category_id=${encodeURIComponent(categoryId)}`);
      }
      fetchEmployees();
      fetch8HourEmployees();
      setShowNewEmployee(false);
    } catch (error) {
      console.error('Fehler beim Erstellen des Mitarbeiters:', error);
    }
  };

  const updatePrice = async (category, itemId, newPrice) => {
    try {
      await axios.put(`${API}/department-admin/menu/${category}/${itemId}?department_id=${currentDepartment.department_id}`, {
        price: parseFloat(newPrice)
      });
      await fetchMenus();
      setSuccessMessage('Preis erfolgreich aktualisiert');
      setShowSuccessNotification(true);
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Preises:', error);
      setSuccessMessage('Fehler beim Aktualisieren des Preises');
      setShowSuccessNotification(true);
    }
  };

  const createMenuItem = async (category, nameOrType, nameOrPrice, priceOrNull, purchaseGrams) => {
    try {
      if (!currentDepartment?.department_id) {
        alert('Fehler: Keine Abteilungs-ID verfügbar');
        return;
      }

      let requestData;
      const departmentId = currentDepartment.department_id;
      
      if (category === 'breakfast') {
        requestData = { roll_type: nameOrType, price: parseFloat(nameOrPrice), department_id: departmentId };
      } else if (category === 'toppings') {
        // New format: toppingId, toppingName, price, purchaseGrams
        requestData = { 
          topping_id: nameOrType, 
          topping_name: nameOrPrice, 
          price: parseFloat(priceOrNull), 
          department_id: departmentId,
          purchase_grams_per_half: purchaseGrams || 0
        };
      } else {
        // For drinks and sweets
        requestData = { name: nameOrType, price: parseFloat(nameOrPrice), department_id: departmentId };
      }

      await axios.post(`${API}/department-admin/menu/${category}`, requestData);
      fetchMenus();
      
      // Close appropriate modals
      if (category === 'drinks') setShowNewDrink(false);
      if (category === 'sweets') setShowNewSweet(false);
      return true;
      
    } catch (error) {
      console.error('Fehler beim Erstellen des Artikels:', error);
      alert('Fehler beim Erstellen des Artikels');
      return false;
    }
  };

  const deleteMenuItem = async (category, itemId) => {
    if (window.confirm('Artikel wirklich löschen?')) {
      try {
        await axios.delete(`${API}/department-admin/menu/${category}/${itemId}?department_id=${currentDepartment.department_id}`);
        fetchMenus();
        alert('Artikel erfolgreich gelöscht');
      } catch (error) {
        console.error('Fehler beim Löschen des Artikels:', error);
        alert('Fehler beim Löschen des Artikels');
      }
    }
  };

  // ERWEITERT: Flexible Payment Function (inkl. Subkonto-Support)
  const processFlexiblePayment = async (paymentData) => {
    try {
      let response;
      
      if (paymentData.isSubaccount) {
        // Subaccount payment
        response = await axios.post(
          `${API}/department-admin/subaccount-payment/${paymentData.employee_id}?admin_department=${paymentData.admin_department}`,
          {
            payment_type: paymentData.payment_type,
            balance_type: paymentData.balance_type,
            amount: parseFloat(paymentData.amount),
            payment_method: paymentData.payment_method || 'cash',
            notes: paymentData.notes || '',
            booked_by: paymentData.booked_by || '',
            network_request_id: paymentData.network_request_id
          }
        );
      } else {
        // Normal payment (existing logic)
        response = await axios.post(
          `${API}/department-admin/flexible-payment/${paymentData.employee_id}?admin_department=${currentDepartment.department_id}`,
          {
            payment_type: paymentData.payment_type,
            amount: parseFloat(paymentData.amount),
            notes: paymentData.notes || '',
            booked_by: paymentData.booked_by || '',
            network_request_id: paymentData.network_request_id
          }
        );
      }
      
      const paymentAction = paymentData.amount >= 0 ? 'Einzahlung' : 'Auszahlung';
      const accountType = paymentData.isSubaccount ? 'Subkonto' : 'Hauptkonto';
      setSuccessMessage(`✅ ${accountType}-${paymentAction} erfolgreich verbucht!\n${response.data.message || response.data.result_description}`);
      setShowSuccessNotification(true);
      
      // Refresh employee data
      await fetchEmployees();
      
      // WICHTIG: Auch 8H-Mitarbeiter neu laden nach Zahlung
      if (paymentData.isSubaccount) {
        await fetch8HourEmployees();
      }
      
      // Call custom callback if provided (for refreshing specific tabs like "Andere WA")
      if (paymentData.onBalanceUpdated && typeof paymentData.onBalanceUpdated === 'function') {
        // Wait a bit for the backend to process, then refresh
        setTimeout(() => {
          paymentData.onBalanceUpdated();
        }, 500);
      }
      
      setShowPaymentModal(false);
      setPaymentEmployeeData(null);
      
    } catch (error) {
      console.error('Fehler bei der Ein-/Auszahlung:', error);
      setSuccessMessage('❌ Fehler bei der Ein-/Auszahlung: ' + (error.response?.data?.detail || error.message));
      setShowSuccessNotification(true);
      return false;
    }
  };

  // NEU: Fetch Extended Order History für aktuelle Abteilung
  const fetchExtendedOrderHistory = async (page = 1) => {
    if (!currentDepartment?.department_id) return;
    
    try {
      const response = await axios.get(`${API}/department-admin/extended-order-history/${currentDepartment.department_id}?page=${page}&page_size=30`);
      setExtendedOrderHistory(response.data.orders);
      setExtendedOrderHistoryPagination({
        page: response.data.page || 1,
        page_size: response.data.page_size || 30,
        total: response.data.total || 0,
        total_pages: response.data.total_pages || 1,
      });
      return response.data;
    } catch (error) {
      console.error('Fehler beim Laden der erweiterten Bestellhistorie:', error);
    }
  };
  
  // NEU: Fetch Help Content (local + global from Payment-Kantine)
  const fetchHelpContent = async () => {
    try {
      const res = await axios.get(`${API}/global-help-content`);
      setGlobalHelpContent(res.data.html_content || '');
      setGlobalHelpLastUpdated(res.data.last_updated || null);
    } catch (error) {
      console.error('Fehler beim Laden der Hilfe-Inhalte:', error);
      setGlobalHelpContent('');
      setGlobalHelpLastUpdated(null);
    }
  };


  return (
    <div className="min-h-screen" style={{ background: '#F5F5F5' }}>
      {onboarding && !onboardingPaused && (tutorialReplay || shouldShowDepartmentOnboarding(onboarding)) && (
        <DepartmentOnboarding
          currentDepartment={currentDepartment}
          initialState={tutorialReplay ? { ...onboarding, current_step: 'tutorial' } : onboarding}
          tutorialOnly={tutorialReplay}
          onCompleted={tutorialReplay ? () => setTutorialReplay(false) : handleOnboardingCompleted}
          onPause={() => tutorialReplay ? setTutorialReplay(false) : setOnboardingPaused(true)}
        />
      )}
      {/* Announcement Banner - visible to admins */}
      <div className={`transition-[margin] duration-300 ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <AnnouncementBanner viewerRole="admin" />
      </div>
      {/* Sidebar Navigation */}
      <DashboardSidebar
        activeTab={activeTab}
        onTabChange={setActiveTab}
        menuItems={getAdminMenuItems(isSingleDepartment || stationModeLoading)}
        dashboardTitle="Admin Dashboard"
        departmentName={currentDepartment.department_name}
        onLogout={logout}
        onBackClick={goBackToEmployeeDashboard}
        backButtonText="Zurück zum Mitarbeiter-Dashboard"
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={setIsSidebarCollapsed}
      />

      {/* Main Content Area - with left margin for sidebar */}
      <div className={`transition-all duration-300 ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'} p-4 sm:p-6 md:p-8 lg:p-8`}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 sm:mb-8 lg:mb-12 gap-4">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800">
              {currentDepartment.department_name} - Admin
            </h1>
            
          </div>

          {!paymentStatusLoading && (
            <PaymentStatusNotice
              status={paymentStatus}
              expanded={paymentDetailsExpanded}
              onToggle={() => setPaymentDetailsExpanded(current => !current)}
            />
          )}

          <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 lg:p-8 min-h-96">
          {activeTab === 'balance-deadlines' && <BalanceDeadlineSettings key={currentDepartment.department_id} departmentId={currentDepartment.department_id} />}
          {activeTab === 'payment' && (
            <PaymentDetails status={paymentStatus} loading={paymentStatusLoading}
              error={paymentStatusError} onRefresh={fetchPaymentStatus} />
          )}
          {activeTab === 'employees' && (
            <EmployeeManagementTab 
              employees={employees}
              eightHourEmployees={eightHourEmployees}
              onCreateEmployee={handleCreateEmployee}
              showNewEmployee={showNewEmployee}
              setShowNewEmployee={setShowNewEmployee}
              currentDepartment={currentDepartment}
              onEmployeeUpdate={() => {
                fetchEmployees();
                fetch8HourEmployees();
              }}
              onCategoriesUpdate={setStatisticsCategories}
              setPaymentEmployeeData={setPaymentEmployeeData}
              setShowPaymentModal={setShowPaymentModal}
              departmentConfig={departmentConfig}
              isSingleDepartment={isSingleDepartment || stationModeLoading}
            />
          )}


          {activeTab === 'statistics' && (
            <StatisticsTab 
              employees={employees}
              eightHourEmployees={eightHourEmployees}
              categories={statisticsCategories}
              currentDepartment={currentDepartment}
              departmentConfig={departmentConfig}
            />
          )}

          {!stationModeLoading && activeTab === 'other-departments' && (
            <OtherDepartmentsTab
              allowLocal={!isSingleDepartment}
              currentDepartment={currentDepartment}
              setPaymentEmployeeData={setPaymentEmployeeData}
              setShowPaymentModal={setShowPaymentModal}
              onBalanceUpdated={handleBalanceUpdated}
              departmentConfig={departmentConfig}
            />
          )}

          {activeTab === 'menu-management' && (
            <UnifiedMenuManagementTab 
              breakfastMenu={breakfastMenu}
              toppingsMenu={toppingsMenu}
              drinksMenu={drinksMenu}
              sweetsMenu={sweetsMenu}
              menuCategories={menuCategories}
              onUpdatePrice={updatePrice}
              onCreateMenuItem={createMenuItem}
              onDeleteMenuItem={deleteMenuItem}
              fetchMenus={fetchMenus}
              currentDepartment={currentDepartment}
              departmentConfig={departmentConfig}
              onUpdateRollTypes={updateRollTypes}
              onUpdateBreakfastItemVisibility={updateBreakfastItemVisibility}
            />
          )}

          {activeTab === 'breakfast-history' && (
            <BreakfastHistoryTab currentDepartment={currentDepartment} departmentConfig={departmentConfig} />
          )}

          {activeTab === 'extended-history' && (
            <ExtendedOrderHistoryTab 
              extendedOrderHistory={extendedOrderHistory}
              historyPagination={extendedOrderHistoryPagination}
              fetchExtendedOrderHistory={fetchExtendedOrderHistory}
              currentDepartment={currentDepartment}
            />
          )}

          {activeTab === 'recipes' && (
            <RecipeLibrary departmentId={currentDepartment.department_id} canEdit />
          )}

          {activeTab === 'help' && (
            <HelpTab
              globalHelpContent={globalHelpContent}
              globalHelpLastUpdated={globalHelpLastUpdated}
              onStartTutorial={() => setTutorialReplay(true)}
            />
          )}

          {activeTab === 'referral' && (
            <ReferralProgramTab currentDepartment={currentDepartment} />
          )}

          {activeTab === 'settings' && (
            <div>
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-1">Einstellungen</h3>
                <p className="text-sm text-gray-500">
                  Dauerhafte Konfiguration der Wachabteilung, Zugänge und Informationen.
                </p>
              </div>

              <AdminSettingsTab
                currentDepartment={currentDepartment}
                departmentConfig={departmentConfig}
                onUpdateDefaultLunchPrice={updateDefaultLunchPrice}
                onUpdateDefaultDinnerPrice={updateDefaultDinnerPrice}
                onToggleDinnerEnabled={toggleDinnerEnabled}
                onUpdateOrganizationFeatures={updateOrganizationFeatures}
                organizationFeaturesDisabled={!departmentConfigLoaded}
              />

            </div>
          )}


        </div>

        {/* New Employee Modal */}
        {showNewEmployee && (
          <NewEmployeeModal
            onCreate={handleCreateEmployee}
            onClose={() => setShowNewEmployee(false)}
            categories={statisticsCategories}
          />
        )}

      {/* ERWEITERT: Flexible Payment Modal (inkl. Subkonto-Support) */}
      {showPaymentModal && paymentEmployeeData && (
        <FlexiblePaymentModal
          employee={paymentEmployeeData.employee}
          paymentType={paymentEmployeeData.paymentType}
          accountLabel={paymentEmployeeData.accountLabel}
          isSubaccount={paymentEmployeeData.isSubaccount || false}
          needsAccountTypeSelection={paymentEmployeeData.needsAccountTypeSelection || false}
          currentDepartment={currentDepartment}
          onClose={() => {
            setShowPaymentModal(false);
            setPaymentEmployeeData(null);
          }}
          onPayment={(data) => processFlexiblePayment({
            ...data,
            onBalanceUpdated: paymentEmployeeData.onBalanceUpdated // Pass callback through
          })}
        />
      )}
      
      {/* Balance Warning Modal */}
      {showBalanceWarning && (
        <BalanceWarningModal
          employeeName={balanceWarningData.employeeName}
          openBalances={balanceWarningData.openBalances}
          onClose={() => {
            setShowBalanceWarning(false);
            setBalanceWarningData({ employeeName: '', openBalances: [] });
            setPendingDeleteEmployee(null);
          }}
          onConfirmDelete={confirmDeleteEmployee}
        />
      )}
      
          {/* Success Notification */}
          {showSuccessNotification && (
            <SuccessNotification
              message={successMessage}
              onClose={() => {
                setShowSuccessNotification(false);
                setSuccessMessage('');
              }}
            />
          )}
        </div>
      </div>
      
      {/* Scroll to Top Button */}
      <ScrollToTop />
    </div>
  );
};

// Employee Management Tab Component
const EmployeeManagementTab = ({ employees, eightHourEmployees = [], onCreateEmployee, showNewEmployee, setShowNewEmployee, currentDepartment, onEmployeeUpdate, onCategoriesUpdate, setPaymentEmployeeData, setShowPaymentModal, departmentConfig = { use_combined_accounts: false }, isSingleDepartment = false }) => {
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [selectedEmployeeForOrders, setSelectedEmployeeForOrders] = useState(null);
  const [sortedEmployees, setSortedEmployees] = useState([]);
  const [draggedEmployeeId, setDraggedEmployeeId] = useState(null);
  const [employeeOrderSaving, setEmployeeOrderSaving] = useState(false);
  
  // Balance Warning Modal State
  const [showBalanceWarning, setShowBalanceWarning] = useState(false);
  const [balanceWarningData, setBalanceWarningData] = useState({ employeeName: '', openBalances: [] });
  const [pendingDeleteEmployee, setPendingDeleteEmployee] = useState(null);
  
  // Bulk Payment State (Sammel-Buchung)
  const [selectedEmployees, setSelectedEmployees] = useState(new Set());
  const [showBulkPaymentModal, setShowBulkPaymentModal] = useState(false);
  
  // Success Notification State
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // v1.16 Employee Categories
  const [categories, setCategories] = useState([]);
  const [activeEmployeeFilter, setActiveEmployeeFilter] = useState('all');
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [showCategoryImport, setShowCategoryImport] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState('#3B82F6');
  const [categoryOrderSaving, setCategoryOrderSaving] = useState(false);
  const [allDepartments, setAllDepartments] = useState([]);
  const [editingEmployeeId, setEditingEmployeeId] = useState(null);
  const [editingEmployeeName, setEditingEmployeeName] = useState('');
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');

  const employeeSearchResults = getAdminEmployeeSearchResults(
    sortedEmployees,
    eightHourEmployees,
    employeeSearchQuery
  );

  const fetchCategories = async ({ preserveOnError = false } = {}) => {
    if (!currentDepartment?.department_id) return;
    try {
      const res = await axios.get(`${API}/departments/${currentDepartment.department_id}/categories`);
      const nextCategories = res.data || [];
      setCategories(nextCategories);
      if (onCategoriesUpdate) onCategoriesUpdate(nextCategories);
    } catch (e) {
      if (!preserveOnError) setCategories([]);
    }
  };
  useEffect(() => { fetchCategories(); }, [currentDepartment?.department_id]);

  const fetchAllDepartments = async () => {
    try {
      const res = await axios.get(`${API}/departments`);
      setAllDepartments(res.data || []);
    } catch (e) {
      setAllDepartments([]);
    }
  };
  useEffect(() => { fetchAllDepartments(); }, []);

  const handleCreateCategory = async () => {
    const name = (newCategoryName || '').trim();
    if (!name) return;
    try {
      await axios.post(`${API}/department-admin/${currentDepartment.department_id}/categories`, { name, color: newCategoryColor });
      setNewCategoryName('');
      await fetchCategories();
    } catch (e) { alert(e?.response?.data?.detail || 'Fehler beim Anlegen.'); }
  };
  const handleRenameCategory = async (id, currentName) => {
    const nn = window.prompt('Neuer Name:', currentName);
    if (nn === null) return;
    const name = nn.trim();
    if (!name || name === currentName) return;
    try {
      await axios.put(`${API}/department-admin/${currentDepartment.department_id}/categories/${id}`, { name });
      await fetchCategories();
    } catch (e) { alert(e?.response?.data?.detail || 'Fehler beim Umbenennen.'); }
  };
  const handleUpdateCategoryColor = async (id, color) => {
    try {
      await axios.put(`${API}/department-admin/${currentDepartment.department_id}/categories/${id}`, { color });
      await fetchCategories();
    } catch (e) {}
  };
  const handleDeleteCategory = async (id, name) => {
    if (!window.confirm(`Kategorie "${name}" löschen? Zugewiesene Mitarbeiter fallen auf "Regulär" zurück.`)) return;
    try {
      await axios.delete(`${API}/department-admin/${currentDepartment.department_id}/categories/${id}`);
      await fetchCategories();
      if (onEmployeeUpdate) onEmployeeUpdate();
    } catch (e) { alert(e?.response?.data?.detail || 'Fehler beim Löschen.'); }
  };
  const handleReorderCategories = async (previous, next) => {
    if (categoryOrderSaving) return;
    setCategories(next);
    setCategoryOrderSaving(true);
    try {
      const result = await saveCategoryOrder(previous, next, async categoryIds => {
        const response = await axios.put(
          `${API}/department-admin/${currentDepartment.department_id}/category-sort-order`,
          { category_ids: categoryIds }
        );
        return response.data;
      });
      setCategories(result.categories);
      if (onCategoriesUpdate) onCategoriesUpdate(result.categories);
      if (result.error) {
        alert(result.error?.response?.data?.detail || 'Reihenfolge konnte nicht gespeichert werden.');
        await fetchCategories({ preserveOnError: true });
      }
    } finally {
      setCategoryOrderSaving(false);
    }
  };
  const handleAssignCategory = async (employee, catId) => {
    try {
      const params = catId ? `?category_id=${encodeURIComponent(catId)}` : '';
      await axios.put(`${API}/department-admin/${currentDepartment.department_id}/employees/${employee.id}/category${params}`);
      if (onEmployeeUpdate) onEmployeeUpdate();
    } catch (e) { alert(e?.response?.data?.detail || 'Fehler beim Zuweisen.'); }
  };

  const updateEmployeeDetails = async (employee, changes) => {
    try {
      await axios.put(
        `${API}/department-admin/${currentDepartment.department_id}/employees/${employee.id}/details`,
        changes
      );
      if (onEmployeeUpdate) onEmployeeUpdate();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Fehler beim Aktualisieren des Mitarbeiters.');
      throw e;
    }
  };

  const startEmployeeNameEdit = (employee) => {
    setEditingEmployeeId(employee.id);
    setEditingEmployeeName(employee.name || '');
  };

  const cancelEmployeeNameEdit = () => {
    setEditingEmployeeId(null);
    setEditingEmployeeName('');
  };

  const saveEmployeeName = async (employee) => {
    const name = editingEmployeeName.trim();
    if (!name) {
      alert('Name darf nicht leer sein');
      return;
    }
    await updateEmployeeDetails(employee, { name });
    cancelEmployeeNameEdit();
  };

  const askHomeDepartmentId = () => {
    const options = allDepartments
      .map((department, index) => `${index + 1}: ${department.name || department.department_name || department.id}`)
      .join('\n');
    const answer = window.prompt(
      `In welche Stamm-Wachabteilung soll der Mitarbeiter wechseln?\n\n${options}\n\nBitte Nummer oder ID eingeben:`,
      currentDepartment?.department_id || ''
    );
    if (answer === null) return null;
    const trimmed = answer.trim();
    const selectedByNumber = allDepartments[Number(trimmed) - 1];
    return selectedByNumber?.id || selectedByNumber?.department_id || trimmed;
  };

  const toggleEmployeeEightHour = async (employee) => {
    const enable8H = !employee.is_8h_service;
    if (enable8H) {
      if (!window.confirm(`"${employee.name}" wirklich auf Übergreifend/8H setzen? Hauptsalden werden dabei auf 0 gesetzt; künftig zählen nur Unterkonten.`)) return;
      await updateEmployeeDetails(employee, { is_8h_service: true });
      return;
    }

    const homeDepartmentId = askHomeDepartmentId();
    if (!homeDepartmentId) return;
    const homeDepartment = allDepartments.find(department => (
      department.id === homeDepartmentId || department.department_id === homeDepartmentId
    ));
    const homeDepartmentName = homeDepartment?.name || homeDepartment?.department_name || homeDepartmentId;
    if (!window.confirm(`"${employee.name}" wirklich nicht mehr Übergreifend/8H führen und als Stamm-Mitarbeiter in "${homeDepartmentName}" setzen?`)) return;
    await updateEmployeeDetails(employee, {
      is_8h_service: false,
      home_department_id: homeDepartmentId,
    });
  };

  const renderEmployeeAdminControls = (employee, accent = 'blue') => (
    <div className="mb-3 space-y-2" onClick={(e) => e.stopPropagation()}>
      <CoffeeFlatControl employeeId={employee.id} departmentId={currentDepartment.department_id} apiBase={API} compact onChanged={onEmployeeUpdate} />
      {editingEmployeeId === employee.id ? (
        <div className="space-y-2 rounded-lg border border-blue-200 bg-blue-50 p-2">
          <input
            type="text"
            value={editingEmployeeName}
            onChange={(e) => setEditingEmployeeName(e.target.value)}
            className="w-full rounded border border-blue-300 px-2 py-1 text-sm"
            autoFocus
            data-testid={`admin-edit-employee-name-${employee.id}`}
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => saveEmployeeName(employee)}
              className="rounded bg-green-600 px-2 py-1 text-xs text-white hover:bg-green-700"
            >
              Speichern
            </button>
            <button
              type="button"
              onClick={cancelEmployeeNameEdit}
              className="rounded bg-gray-500 px-2 py-1 text-xs text-white hover:bg-gray-600"
            >
              Abbrechen
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => startEmployeeNameEdit(employee)}
          className="text-xs font-medium text-blue-700 hover:text-blue-900"
          data-testid={`admin-rename-employee-${employee.id}`}
        >
          ✏️ Namen bearbeiten
        </button>
      )}
      {!isSingleDepartment && <div className="grid grid-cols-2 gap-1 rounded-lg bg-gray-100 p-1">
        <button
          type="button"
          onClick={() => employee.is_8h_service && toggleEmployeeEightHour(employee)}
          className={`rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
            !employee.is_8h_service ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
          }`}
          data-testid={`admin-set-regular-${employee.id}`}
        >
          Regulär
        </button>
        <button
          type="button"
          onClick={() => !employee.is_8h_service && toggleEmployeeEightHour(employee)}
          className={`rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
            employee.is_8h_service
              ? `bg-white shadow-sm ${accent === 'orange' ? 'text-orange-700' : 'text-blue-700'}`
              : 'text-gray-600 hover:text-gray-900'
          }`}
          data-testid={`admin-set-8h-${employee.id}`}
        >
          Übergreifend
        </button>
      </div>}
    </div>
  );

  // Initialize sorted employees when employees prop changes
  useEffect(() => {
    setSortedEmployees([...employees]);
  }, [employees]);

  // Drag and Drop handlers
  const handleDragStart = (e, employeeId) => {
    if (employeeSearchResults.active || employeeOrderSaving) {
      e.preventDefault();
      return;
    }
    setDraggedEmployeeId(employeeId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', employeeId);
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedEmployeeId(null);
  };

  const handleDragOver = (e, targetEmployee) => {
    const draggedEmployee = sortedEmployees.find(employee => employee.id === draggedEmployeeId);
    if (
      employeeSearchResults.active
      || !draggedEmployee
      || (draggedEmployee.category_id || null) !== (targetEmployee.category_id || null)
    ) {
      return;
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetEmployeeId) => {
    e.preventDefault();
    const previousEmployees = sortedEmployees;
    const result = reorderEmployeeWithinCategory(
      previousEmployees,
      draggedEmployeeId,
      targetEmployeeId,
    );
    setDraggedEmployeeId(null);

    if (!result.changed) {
      return;
    }

    setSortedEmployees(result.employees);
    setEmployeeOrderSaving(true);
    try {
      const persisted = await persistEmployeeOrder(
        previousEmployees,
        result.employees,
        async employeeIds => {
          const response = await axios.put(
            `${API}/departments/${currentDepartment.department_id}/employees/sort-order`,
            employeeIds,
          );
          return response.data;
        },
      );
      setSortedEmployees(persisted.employees);
      if (persisted.error) {
        console.error('Fehler beim Speichern der Sortierung:', persisted.error);
        alert('Fehler beim Speichern der Sortierung. Die vorherige Reihenfolge wurde wiederhergestellt.');
      } else if (onEmployeeUpdate) {
        onEmployeeUpdate();
      }
    } finally {
      setEmployeeOrderSaving(false);
    }
  };

  // LEGACY: Keep old function for backward compatibility (but mark as deprecated)
  const markAsPaid = async (employee, balanceType) => {
    const balanceAmount = balanceType === 'breakfast' ? employee.breakfast_balance : employee.drinks_sweets_balance;
    const balanceLabel = balanceType === 'breakfast' ? 'Essen' : 'Getränke/Snacks';
    
    if (balanceAmount <= 0) {
      alert('Kein Saldo zum Zurücksetzen vorhanden');
      return;
    }
    
    if (window.confirm(`LEGACY: ${balanceLabel}-Saldo von ${balanceAmount.toFixed(2)} € für ${employee.name} als bezahlt markieren?`)) {
      try {
        await axios.post(`${API}/department-admin/payment/${employee.id}?payment_type=${balanceType}&amount=${balanceAmount}&admin_department=${currentDepartment.department_name}`);
        alert('Zahlung erfolgreich verbucht (Legacy-Modus)');
        // Refresh employee data instead of full page reload
        if (onEmployeeUpdate) {
          onEmployeeUpdate();
        }
      } catch (error) {
        console.error('Fehler beim Verbuchen der Zahlung:', error);
        alert('Fehler beim Verbuchen der Zahlung');
      }
    }
  };

  const checkEmployeeBalancesBeforeDelete = async (employee) => {
    try {
      // Hole alle Balances des Mitarbeiters (Haupt- und Subkonten)
      const response = await axios.get(`${API}/employees/${employee.id}/all-balances`);
      const balances = response.data;
      
      const is8HService = balances.is_8h_service || employee.is_8h_service || false;
      
      const openBalances = [];
      
      // Für 8H-Mitarbeiter: Nur Subkonten prüfen (Hauptkonten sollten immer 0 sein)
      // Für normale Mitarbeiter: Hauptkonten prüfen
      if (!is8HService) {
        // Prüfe Hauptkonto-Balances - API returns main_balances.breakfast/drinks_sweets
        const mainBreakfastBalance = parseFloat(balances.main_balances?.breakfast || 0);
        const mainDrinksBalance = parseFloat(balances.main_balances?.drinks_sweets || 0);
        
        if (mainBreakfastBalance !== 0) {
          openBalances.push(`Hauptkonto Essen: ${mainBreakfastBalance.toFixed(2)}€`);
        }
        
        if (mainDrinksBalance !== 0) {
          openBalances.push(`Hauptkonto Getränke/Snacks: ${mainDrinksBalance.toFixed(2)}€`);
        }
      }
      
      // Prüfe Subkonto-Balances (für ALLE Mitarbeiter, inklusive 8H)
      if (balances.subaccount_balances) {
        for (const [deptId, subBalances] of Object.entries(balances.subaccount_balances)) {
          const subBreakfast = parseFloat(subBalances.breakfast || 0);
          const subDrinks = parseFloat(subBalances.drinks || 0);
          
          if (subBreakfast !== 0) {
            // Extract the department name correctly: fw4abteilung2 -> 2. Wachabteilung
            const match = deptId.match(/fw\d+abteilung(\d+)/);
            const deptName = match ? `${match[1]}. Wachabteilung` : deptId;
            openBalances.push(`${is8HService ? '🕐 ' : ''}Subkonto ${deptName} Essen: ${subBreakfast.toFixed(2)}€`);
          }
          
          if (subDrinks !== 0) {
            // Extract the department name correctly: fw4abteilung2 -> 2. Wachabteilung
            const match = deptId.match(/fw\d+abteilung(\d+)/);
            const deptName = match ? `${match[1]}. Wachabteilung` : deptId;
            openBalances.push(`${is8HService ? '🕐 ' : ''}Subkonto ${deptName} Getränke/Snacks: ${subDrinks.toFixed(2)}€`);
          }
        }
      }
      
      return openBalances;
    } catch (error) {
      console.error('Fehler beim Prüfen der Mitarbeiter-Balances:', error);
      // Im Fehlerfall erlauben wir das Löschen nicht
      return ['Fehler beim Prüfen der Balances'];
    }
  };

  const deleteEmployee = async (employee) => {
    // Prüfe zuerst alle Balances
    const openBalances = await checkEmployeeBalancesBeforeDelete(employee);
    
    if (openBalances.length > 0) {
      // Show warning but allow deletion
      setPendingDeleteEmployee(employee);
      setBalanceWarningData({
        employeeName: employee.name,
        openBalances: openBalances
      });
      setShowBalanceWarning(true);
      return;
    }
    
    if (window.confirm(`Mitarbeiter ${employee.name} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden und löscht auch alle Bestellungen des Mitarbeiters.`)) {
      try {
        await axios.delete(`${API}/department-admin/employees/${employee.id}`);
        alert('Mitarbeiter erfolgreich gelöscht');
        // Refresh employee data instead of full page reload
        if (onEmployeeUpdate) {
          onEmployeeUpdate();
        }
      } catch (error) {
        console.error('Fehler beim Löschen des Mitarbeiters:', error);
        const errorMessage = error.response?.data?.detail || 'Fehler beim Löschen des Mitarbeiters';
        alert(errorMessage);
      }
    }
  };

  const confirmDeleteEmployee = async () => {
    if (!pendingDeleteEmployee) return;
    const employee = pendingDeleteEmployee;
    setShowBalanceWarning(false);
    setBalanceWarningData({ employeeName: '', openBalances: [] });
    setPendingDeleteEmployee(null);
    try {
      await axios.delete(`${API}/department-admin/employees/${employee.id}`);
      alert('Mitarbeiter erfolgreich gelöscht');
      if (onEmployeeUpdate) {
        onEmployeeUpdate();
      }
    } catch (error) {
      console.error('Fehler beim Löschen des Mitarbeiters:', error);
      const errorMessage = error.response?.data?.detail || 'Fehler beim Löschen des Mitarbeiters';
      alert(errorMessage);
    }
  };

  // Bulk Payment Handlers (Sammel-Buchung)
  const handleSelectEmployee = (employeeId) => {
    setSelectedEmployees(prev => {
      const newSet = new Set(prev);
      if (newSet.has(employeeId)) {
        newSet.delete(employeeId);
      } else {
        newSet.add(employeeId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedEmployees.size === sortedEmployees.length) {
      // Deselect all
      setSelectedEmployees(new Set());
    } else {
      // Select all
      setSelectedEmployees(new Set(sortedEmployees.map(e => e.id)));
    }
  };

  const handleDeselectAll = () => {
    setSelectedEmployees(new Set());
  };

  const handleBulkPayment = async (paymentData) => {
    try {
      const response = await axios.post(
        `${API}/department-admin/bulk-payment`,
        {
          employee_ids: Array.from(selectedEmployees),
          amount: paymentData.amount,
          balance_type: paymentData.balance_type,
          notes: paymentData.notes || '',
          booked_by: paymentData.booked_by || '',
          payment_method: paymentData.payment_method || 'cash'
        },
        {
          params: {
            admin_department: currentDepartment.department_id
          }
        }
      );

      setSuccessMessage(response.data.message);
      setShowSuccessNotification(true);
      
      // Refresh employees
      if (onEmployeeUpdate) {
        onEmployeeUpdate();
      }
      
      // Clear selection
      setSelectedEmployees(new Set());
      
    } catch (error) {
      console.error('Fehler bei Sammel-Buchung:', error);
      alert(`Fehler: ${error.response?.data?.detail || error.message}`);
    }
  };


  const viewEmployeeOrders = (employee) => {
    setSelectedEmployeeForOrders(employee);
    setShowOrdersModal(true);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0 mb-6">
        <div>
          <h3 className="text-lg font-semibold">Mitarbeiter verwalten</h3>
          <p className="text-sm text-gray-600 mt-1">
            <svg className="inline w-4 h-4 mr-1" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8,18H11V15H8V18M8,14H11V10H8V14M8,9H11V6H8V9M13,18H16V15H13V18M13,14H16V10H13V14M13,9H16V6H13V9Z" />
            </svg>
            Mitarbeiter per Drag & Drop sortieren
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowCategoryManager(v => !v)}
            className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-lg text-sm sm:text-base border border-gray-300"
            data-testid="toggle-category-manager"
          >
            🏷️ Kategorien
          </button>
          <button
            onClick={() => setShowNewEmployee(true)}
            className="bg-gray-800 text-white px-4 py-2 rounded-lg hover:bg-gray-900 text-sm sm:text-base"
          >
            Neuer Mitarbeiter
          </button>
        </div>
      </div>

      {showCategoryManager && (
        <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50" data-testid="category-manager">
          <h4 className="font-semibold text-gray-800 mb-3">Kategorien verwalten</h4>
          <p className="text-xs text-gray-500 mb-3">
            Die Sortierung gilt für alle Anzeigebereiche.
          </p>
          {categories.length === 0 && <p className="text-sm text-gray-500 italic mb-3">Noch keine Kategorien angelegt.</p>}
          <EmployeeCategoryManager
            categories={categories}
            saving={categoryOrderSaving}
            onReorder={handleReorderCategories}
            onColorChange={handleUpdateCategoryColor}
            onRename={handleRenameCategory}
            onDelete={handleDeleteCategory}
          />
          <div className="flex flex-wrap items-center gap-2">
            <input type="color" value={newCategoryColor} onChange={(e) => setNewCategoryColor(e.target.value)}
              className="w-8 h-8 rounded cursor-pointer border border-gray-300" title="Farbe" data-testid="new-category-color" />
            <input type="text" value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreateCategory(); }}
              placeholder="Neue Kategorie (z.B. Notärzte)"
              className="flex-1 min-w-[180px] px-3 py-2 border border-gray-300 rounded text-sm"
              data-testid="new-category-name" />
            <button onClick={handleCreateCategory}
              className="px-3 py-2 bg-gray-800 text-white rounded-lg text-sm hover:bg-gray-900"
              data-testid="new-category-submit">+ Anlegen</button>
            {!isSingleDepartment && <button
              type="button"
              onClick={() => setShowCategoryImport(true)}
              className="px-3 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-sm hover:bg-blue-100"
              data-testid="open-category-import"
            >
              Aus anderer Wachabteilung übernehmen
            </button>}
          </div>
        </div>
      )}

      {!isSingleDepartment && showCategoryImport && (
        <CategoryImportDialog
          departmentId={currentDepartment.department_id}
          departments={allDepartments}
          onClose={() => setShowCategoryImport(false)}
          onApplied={async () => {
            setShowCategoryImport(false);
            await fetchCategories();
            if (onEmployeeUpdate) onEmployeeUpdate();
          }}
        />
      )}

      <div className="relative mb-3">
        <input
          type="text"
          value={employeeSearchQuery}
          onChange={(event) => setEmployeeSearchQuery(event.target.value)}
          placeholder="🔍 Mitarbeiter suchen…"
          className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500"
          data-testid="admin-employee-search-input"
        />
        {employeeSearchQuery && (
          <button
            type="button"
            onClick={() => setEmployeeSearchQuery('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 text-xl leading-none"
            title="Suche zurücksetzen"
            data-testid="admin-employee-search-clear"
          >×</button>
        )}
      </div>

      {(() => {
        if (employeeSearchResults.active) {
          return (
            <p className="mb-5 text-xs text-gray-500">
              Suche aktiv — Alle Mitarbeiter werden durchsucht.
            </p>
          );
        }

        const filterTabs = [
          {
            key: 'regular',
            label: 'Regulär',
            count:
              sortedEmployees.filter(isRegularDepartmentEmployee).length
              + eightHourEmployees.filter(isRegularDepartmentEmployee).length,
          },
          ...categories.map(category => ({
            key: `category-${category.id}`,
            label: category.name,
            color: category.color || '#6B7280',
            count:
              sortedEmployees.filter(employee => employee.category_id === category.id).length
              + eightHourEmployees.filter(employee => employee.category_id === category.id).length,
          })),
          {
            key: 'all',
            label: 'Alle',
            count: sortedEmployees.length + eightHourEmployees.length,
          },
        ].filter(tab => tab.key === 'all' || tab.count > 0);

        return (
          <div className="mb-5 flex flex-wrap gap-2" data-testid="admin-employee-category-filters">
            {filterTabs.map(tab => {
              const isActive = activeEmployeeFilter === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveEmployeeFilter(tab.key)}
                  className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                    isActive
                      ? 'border-gray-800 bg-gray-800 text-white'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  data-testid={`admin-employee-filter-${tab.key}`}
                >
                  {tab.color && (
                    <span
                      className="h-2.5 w-2.5 rounded-full border border-black/10"
                      style={{ backgroundColor: tab.color }}
                    />
                  )}
                  <span>{tab.label}</span>
                  <span className={isActive ? 'text-gray-300' : 'text-gray-400'}>({tab.count})</span>
                </button>
              );
            })}
          </div>
        );
      })()}

      {/* Selection Toolbar - Nur Alle/Keine Buttons */}
      {selectedEmployees.size > 0 && (
        <div className="mb-4 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm sm:text-base">
              <span className="font-semibold text-blue-700">
                ☑️ {selectedEmployees.size} ausgewählt
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSelectAll}
                className="px-3 py-1.5 text-xs sm:text-sm bg-white border border-blue-300 text-blue-700 rounded hover:bg-blue-50 transition-colors"
              >
                Alle
              </button>
              <button
                onClick={handleDeselectAll}
                className="px-3 py-1.5 text-xs sm:text-sm bg-white border border-blue-300 text-blue-700 rounded hover:bg-blue-50 transition-colors"
              >
                Keine
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {(() => {
          const categoryRank = (employee) => employee.category_id
            ? categories.findIndex(category => category.id === employee.category_id) + 1
            : 0;
          const byCategoryThenOrder = (a, b) =>
            categoryRank(a) - categoryRank(b)
            || (a.sort_order || 0) - (b.sort_order || 0)
            || a.name.localeCompare(b.name);
          const selectedCategoryId = activeEmployeeFilter.startsWith('category-')
            ? activeEmployeeFilter.slice('category-'.length)
            : null;
          const matchesActiveFilter = (employee) => {
            if (employeeSearchResults.active) return true;
            if (activeEmployeeFilter === 'all') return true;
            if (activeEmployeeFilter === 'regular') {
              return isRegularDepartmentEmployee(employee);
            }
            return employee.category_id === selectedCategoryId;
          };
          const regularEmployees = employeeSearchResults.departmentEmployees
            .filter(emp => matchesActiveFilter(emp))
            .sort(byCategoryThenOrder);
          const guestEmployees = [];
          const crossDepartmentEmployees = [...employeeSearchResults.crossDepartmentEmployees]
            .filter(emp => matchesActiveFilter(emp))
            .sort(byCategoryThenOrder);
          const displaySections = buildNonEmptyEmployeeDisplaySections(
            categories,
            sortedEmployees,
            eightHourEmployees,
          );
          const sectionRank = section => section.key === 'regular'
            ? 0
            : categories.findIndex(category => `category-${category.id}` === section.key) + 1;
          
          return (
            <>
              {employeeSearchResults.active
                && regularEmployees.length === 0
                && guestEmployees.length === 0
                && crossDepartmentEmployees.length === 0 && (
                  <p
                    className="col-span-full py-8 text-center text-sm text-gray-500"
                    data-testid="admin-employee-search-empty"
                  >
                    Keine Mitarbeiter gefunden für „{employeeSearchQuery.trim()}“.
                  </p>
                )}

              {!employeeSearchResults.active && activeEmployeeFilter === 'all' && displaySections.map((section, index) => (
                <div
                  key={`heading-${section.key}`}
                  className={`col-span-full flex items-center gap-3 ${index === 0 ? 'mt-1' : 'mt-5'}`}
                  style={{ order: sectionRank(section) * 2 }}
                  data-testid={`admin-employee-section-${section.key}`}
                >
                  <span
                    className="h-2.5 w-2.5 rounded-full border border-black/10"
                    style={{ backgroundColor: section.color || '#6B7280' }}
                  />
                  <span className="text-sm font-semibold text-gray-700">{section.label}</span>
                  <span className="text-xs text-gray-400">({section.entries.length})</span>
                  <div className="h-px flex-1 bg-gray-200" />
                </div>
              ))}

              {/* Regular employees */}
              {regularEmployees.map((employee) => {
                // Find the original index in sortedEmployees for drag & drop and display
                const originalIndex = sortedEmployees.findIndex(emp => emp.id === employee.id);
                const assignedCategory = categories.find(category => category.id === employee.category_id);
                
                return (
                  <div 
                    key={employee.id} 
                    className={`bg-white border border-gray-200 rounded-xl p-4 transition-all duration-200 shadow-sm ${assignedCategory ? 'border-l-4' : ''} ${
                      draggedEmployeeId === employee.id ? 'shadow-lg border-blue-400 bg-blue-50' : 'hover:shadow-md'
                    }`}
                    style={{
                      order: categoryRank(employee) * 2 + 1,
                      ...(assignedCategory ? { borderLeftColor: assignedCategory.color || '#6B7280' } : {}),
                    }}
                    draggable={!employeeSearchResults.active && !employeeOrderSaving}
                    onDragStart={(e) => handleDragStart(e, employee.id)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleDragOver(e, employee)}
                    onDrop={(e) => handleDrop(e, employee.id)}
                  >
                    {/* Checkbox and Drag Handle */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={selectedEmployees.has(employee.id)}
                          onChange={() => handleSelectEmployee(employee.id)}
                          className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <h4 className="font-semibold text-base sm:text-lg">{employee.name}</h4>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div 
                          className="cursor-move text-gray-400 hover:text-gray-600"
                          title="Zum Sortieren ziehen"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8,18H11V15H8V18M8,14H11V10H8V14M8,9H11V6H8V9M13,18H16V15H13V18M13,14H16V10H13V14M13,9H16V6H13V9Z" />
                          </svg>
                        </div>
                        <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">
                          #{originalIndex + 1}
                        </span>
                      </div>
                    </div>
                    
                    {/* v1.16 Kategorie-Zuweisung */}
                    <div className="mb-3 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <label className="text-xs text-gray-500 whitespace-nowrap">Kategorie:</label>
                      <select
                        value={employee.category_id || ''}
                        onChange={(e) => handleAssignCategory(employee, e.target.value || null)}
                        className="flex-1 text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:border-blue-500 bg-white"
                        data-testid={`employee-category-${employee.id}`}
                      >
                        <option value="">— Ohne Kategorie —</option>
                        {categories.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>

                    {renderEmployeeAdminControls(employee)}

                    {/* Balance Display - Dynamic based on department config */}
                    {departmentConfig.use_combined_accounts ? (
                      /* COMBINED ACCOUNTS: Zeige nur ein Konto */
                      <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">Gesamtsaldo:</span>
                          <span className={`font-bold ${(employee.combined_balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {(employee.combined_balance || 0) >= 0 ? '+' : ''}{(employee.combined_balance || 0).toFixed(2)} €
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Essen, Getränke & Snacks</p>
                      </div>
                    ) : (
                      /* SEPARATE ACCOUNTS: Zeige getrennte Konten */
                      <>
                        {/* Breakfast Balance */}
                        <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium">Essen:</span>
                            <span className={`font-bold ${employee.breakfast_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {employee.breakfast_balance >= 0 ? '+' : ''}{employee.breakfast_balance.toFixed(2)} €
                            </span>
                          </div>
                        </div>
                        
                        {/* Drinks/Sweets Balance */}
                        <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium">Getränke/Snacks:</span>
                            <span className={`font-bold ${employee.drinks_sweets_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {employee.drinks_sweets_balance >= 0 ? '+' : ''}{employee.drinks_sweets_balance.toFixed(2)} €
                            </span>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Order Management Button */}
                    <div className="mb-3">
                      <button
                        onClick={() => {
                          setSelectedEmployeeForOrders(employee);
                          setShowOrdersModal(true);
                        }}
                        className="w-full bg-gray-800 text-white text-sm py-2 px-3 rounded-lg hover:bg-gray-900"
                      >
                        📋 Verlauf anzeigen
                      </button>
                    </div>

                    {/* Delete Button */}
                    <div className="mt-3">
                      <button
                        onClick={() => deleteEmployee(employee)}
                        className="w-full bg-white border border-red-200 text-red-700 text-xs py-2 px-2 rounded-lg hover:bg-red-50"
                      >
                        Mitarbeiter löschen
                      </button>
                    </div>

                    {/* Payment Buttons - Dynamic based on department config */}
                    {departmentConfig.use_combined_accounts ? (
                      /* COMBINED: Nur ein Button für Gesamtsaldo */
                      <div className="mt-2">
                        <button
                          onClick={() => {
                            setPaymentEmployeeData({
                              employee: employee,
                              paymentType: 'combined',
                              accountLabel: 'Gesamtsaldo'
                            });
                            setShowPaymentModal(true);
                          }}
                          className="w-full bg-gray-100 border border-gray-200 text-gray-700 text-xs py-2 px-2 rounded-lg hover:bg-gray-200"
                        >
                          💰 Saldo verwalten
                        </button>
                      </div>
                    ) : (
                      /* SEPARATE: Zwei Buttons für getrennte Konten */
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <button
                          onClick={() => {
                            setPaymentEmployeeData({
                              employee: employee,
                              paymentType: 'breakfast',
                              accountLabel: 'Essen'
                            });
                            setShowPaymentModal(true);
                          }}
                          className="bg-gray-100 border border-gray-200 text-gray-700 text-xs py-1.5 px-2 rounded-lg hover:bg-gray-200"
                        >
                          💰 Essen
                        </button>
                        <button
                          onClick={() => {
                            setPaymentEmployeeData({
                              employee: employee,
                              paymentType: 'drinks_sweets',
                              accountLabel: 'Getränke'
                            });
                            setShowPaymentModal(true);
                          }}
                          className="bg-gray-100 border border-gray-200 text-gray-700 text-xs py-1.5 px-2 rounded-lg hover:bg-gray-200"
                        >
                          💰 Getränke
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              
              {/* Visual separator if guests exist */}
              {guestEmployees.length > 0 && (
                <div className="col-span-full">
                  <div className="flex items-center my-6">
                    <div className="flex-1 border-t-2 border-gray-300"></div>
                    <div className="px-4 text-sm font-medium text-gray-500 bg-white">
                      👤 Gäste
                    </div>
                    <div className="flex-1 border-t-2 border-gray-300"></div>
                  </div>
                </div>
              )}
              
              {/* Guest employees */}
              {guestEmployees.map((employee) => {
                // Find the original index in sortedEmployees for drag & drop and display
                const originalIndex = sortedEmployees.findIndex(emp => emp.id === employee.id);
                const assignedCategory = categories.find(category => category.id === employee.category_id);
                
                return (
                  <div 
                    key={employee.id} 
                    className={`bg-white border border-gray-200 rounded-xl p-4 transition-all duration-200 shadow-sm border-l-4 ${
                      draggedEmployeeId === employee.id ? 'shadow-lg border-blue-400 bg-blue-50' : 'hover:shadow-md'
                    }`}
                    style={{
                      order: categoryRank(employee) * 2 + 1,
                      borderLeftColor: assignedCategory?.color || '#94A3B8',
                    }}
                    draggable={!employeeSearchResults.active && !employeeOrderSaving}
                    onDragStart={(e) => handleDragStart(e, employee.id)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleDragOver(e, employee)}
                    onDrop={(e) => handleDrop(e, employee.id)}
                  >
                    {/* Checkbox and Drag Handle */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={selectedEmployees.has(employee.id)}
                          onChange={() => handleSelectEmployee(employee.id)}
                          className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <h4 className="font-semibold text-base sm:text-lg">
                          {employee.name} <span className="text-sm text-blue-600 font-normal">👤 Gast</span>
                        </h4>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div 
                          className="cursor-move text-gray-400 hover:text-gray-600"
                          title="Zum Sortieren ziehen"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8,18H11V15H8V18M8,14H11V10H8V14M8,9H11V6H8V9M13,18H16V15H13V18M13,14H16V10H13V14M13,9H16V6H13V9Z" />
                          </svg>
                        </div>
                        <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">
                          #{originalIndex + 1}
                        </span>
                      </div>
                    </div>
                    
                    {/* v1.16 Kategorie-Zuweisung */}
                    <div className="mb-3 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <label className="text-xs text-gray-500 whitespace-nowrap">Kategorie:</label>
                      <select
                        value={employee.category_id || ''}
                        onChange={(e) => handleAssignCategory(employee, e.target.value || null)}
                        className="flex-1 text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:border-blue-500 bg-white"
                        data-testid={`employee-category-${employee.id}`}
                      >
                        <option value="">— Ohne Kategorie —</option>
                        {categories.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>

                    {renderEmployeeAdminControls(employee)}

                    {/* Balance Display - Dynamic based on department config */}
                    {departmentConfig.use_combined_accounts ? (
                      /* COMBINED ACCOUNTS: Zeige nur ein Konto */
                      <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">Gesamtsaldo:</span>
                          <span className={`font-bold ${(employee.combined_balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {(employee.combined_balance || 0) >= 0 ? '+' : ''}{(employee.combined_balance || 0).toFixed(2)} €
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Essen, Getränke & Snacks</p>
                      </div>
                    ) : (
                      /* SEPARATE ACCOUNTS: Zeige getrennte Konten */
                      <>
                        {/* Breakfast Balance */}
                        <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium">Essen:</span>
                            <span className={`font-bold ${employee.breakfast_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {employee.breakfast_balance >= 0 ? '+' : ''}{employee.breakfast_balance.toFixed(2)} €
                            </span>
                          </div>
                        </div>
                        
                        {/* Drinks/Sweets Balance */}
                        <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium">Getränke/Snacks:</span>
                            <span className={`font-bold ${employee.drinks_sweets_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {employee.drinks_sweets_balance >= 0 ? '+' : ''}{employee.drinks_sweets_balance.toFixed(2)} €
                            </span>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Order Management Button */}
                    <div className="mb-3">
                      <button
                        onClick={() => {
                          setSelectedEmployeeForOrders(employee);
                          setShowOrdersModal(true);
                        }}
                        className="w-full bg-gray-800 text-white text-sm py-2 px-3 rounded-lg hover:bg-gray-900"
                      >
                        📋 Verlauf anzeigen
                      </button>
                    </div>

                    {/* Delete Button */}
                    <div className="mt-3">
                      <button
                        onClick={() => deleteEmployee(employee)}
                        className="w-full bg-white border border-red-200 text-red-700 text-xs py-2 px-2 rounded-lg hover:bg-red-50"
                      >
                        Mitarbeiter löschen
                      </button>
                    </div>

                    {/* Payment Buttons - Dynamic based on department config */}
                    {departmentConfig.use_combined_accounts ? (
                      /* COMBINED: Nur ein Button für Gesamtsaldo */
                      <div className="mt-2">
                        <button
                          onClick={() => {
                            setPaymentEmployeeData({
                              employee: employee,
                              paymentType: 'combined',
                              accountLabel: 'Gesamtsaldo'
                            });
                            setShowPaymentModal(true);
                          }}
                          className="w-full bg-gray-100 border border-gray-200 text-gray-700 text-xs py-2 px-2 rounded-lg hover:bg-gray-200"
                        >
                          💰 Saldo verwalten
                        </button>
                      </div>
                    ) : (
                      /* SEPARATE: Zwei Buttons für getrennte Konten */
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <button
                          onClick={() => {
                            setPaymentEmployeeData({
                              employee: employee,
                              paymentType: 'breakfast',
                              accountLabel: 'Essen'
                            });
                            setShowPaymentModal(true);
                          }}
                          className="bg-gray-100 border border-gray-200 text-gray-700 text-xs py-1.5 px-2 rounded-lg hover:bg-gray-200"
                        >
                          💰 Essen
                        </button>
                        <button
                          onClick={() => {
                            setPaymentEmployeeData({
                              employee: employee,
                              paymentType: 'drinks_sweets',
                              accountLabel: 'Getränke'
                            });
                            setShowPaymentModal(true);
                          }}
                          className="bg-gray-100 border border-gray-200 text-gray-700 text-xs py-1.5 px-2 rounded-lg hover:bg-gray-200"
                        >
                          💰 Getränke
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              
              {/* NEU: 8H-Dienst employees */}
              {crossDepartmentEmployees.map((employee) => (
                <div 
                  key={`8h-${employee.id}`}
                  className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-all duration-200 shadow-sm"
                  style={{
                    order: categoryRank(employee) * 2 + 1,
                    borderLeftWidth: employee.category_id ? '4px' : undefined,
                    borderLeftColor: categories.find(category => category.id === employee.category_id)?.color,
                  }}
                >
                  {/* Checkbox and Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={selectedEmployees.has(employee.id)}
                        onChange={() => handleSelectEmployee(employee.id)}
                        className="w-5 h-5 text-orange-600 rounded focus:ring-2 focus:ring-orange-500 cursor-pointer"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <h4 className="font-semibold text-base sm:text-lg">{employee.name}</h4>
                    </div>
                  </div>
                  
                  {renderEmployeeAdminControls(employee, 'orange')}

                  <div className="mb-3 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <label className="text-xs text-gray-500 whitespace-nowrap">Kategorie:</label>
                    <select
                      value={employee.category_id || ''}
                      onChange={(e) => handleAssignCategory(employee, e.target.value || null)}
                      className="flex-1 text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:border-orange-500 bg-white"
                      data-testid={`employee-category-${employee.id}`}
                    >
                      <option value="">— Ohne Kategorie —</option>
                      {categories.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Subaccount Balance Display - Combined or Separate */}
                  {/* Use employee.use_combined_accounts from API response (most reliable) or fall back to departmentConfig */}
                  {(employee.use_combined_accounts !== undefined ? employee.use_combined_accounts : departmentConfig.use_combined_accounts) ? (
                    /* COMBINED ACCOUNTS: Show only combined balance */
                    <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Gesamtsaldo (Subkonto):</span>
                        <span className={`font-bold ${(employee.subaccount_combined_balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {(employee.subaccount_combined_balance || 0) >= 0 ? '+' : ''}{(employee.subaccount_combined_balance || 0).toFixed(2)} €
                        </span>
                      </div>
                    </div>
                  ) : (
                    /* SEPARATE ACCOUNTS: Show breakfast and drinks separately */
                    <>
                      {/* Subaccount Breakfast Balance */}
                      <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">Essen (Subkonto):</span>
                          <span className={`font-bold ${(employee.subaccount_breakfast_balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {(employee.subaccount_breakfast_balance || 0) >= 0 ? '+' : ''}{(employee.subaccount_breakfast_balance || 0).toFixed(2)} €
                          </span>
                        </div>
                      </div>
                      
                      {/* Subaccount Drinks Balance */}
                      <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">Getränke/Snacks (Subkonto):</span>
                          <span className={`font-bold ${(employee.subaccount_drinks_balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {(employee.subaccount_drinks_balance || 0) >= 0 ? '+' : ''}{(employee.subaccount_drinks_balance || 0).toFixed(2)} €
                          </span>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Delete Button */}
                  <div className="mt-3">
                    <button
                      onClick={() => deleteEmployee(employee)}
                      className="w-full bg-white border border-red-200 text-red-700 text-xs py-2 px-2 rounded-lg hover:bg-red-50"
                    >
                      Mitarbeiter löschen
                    </button>
                  </div>

                  {/* Payment Buttons - Combined or Separate */}
                  {departmentConfig.use_combined_accounts ? (
                    /* COMBINED ACCOUNTS: Single payment button */
                    <div className="mt-3">
                      <button
                        onClick={() => {
                          setPaymentEmployeeData({
                            employee: employee,
                            paymentType: 'combined',
                            accountLabel: 'Gesamtkonto (Subkonto)',
                            isSubaccount: true  // WICHTIG: Für 8H-Mitarbeiter!
                          });
                          setShowPaymentModal(true);
                        }}
                        className="w-full bg-gray-100 border border-gray-200 text-gray-700 text-xs py-1.5 px-2 rounded-lg hover:bg-gray-200"
                      >
                        💰 Ein-/Auszahlung
                      </button>
                    </div>
                  ) : (
                    /* SEPARATE ACCOUNTS: Two payment buttons */
                    <div className="mt-3 flex gap-2">
                      <button
                        onClick={() => {
                          setPaymentEmployeeData({
                            employee: employee,
                            paymentType: 'breakfast',
                            accountLabel: 'Frühstück (Subkonto)',
                            isSubaccount: true  // WICHTIG: Für 8H-Mitarbeiter!
                          });
                          setShowPaymentModal(true);
                        }}
                        className="flex-1 bg-gray-100 border border-gray-200 text-gray-700 text-xs py-1.5 px-2 rounded-lg hover:bg-gray-200"
                      >
                        💰 Frühstück
                      </button>
                      <button
                        onClick={() => {
                          setPaymentEmployeeData({
                            employee: employee,
                            paymentType: 'drinks_sweets',
                            accountLabel: 'Getränke (Subkonto)',
                            isSubaccount: true  // WICHTIG: Für 8H-Mitarbeiter!
                          });
                          setShowPaymentModal(true);
                        }}
                        className="flex-1 bg-gray-100 border border-gray-200 text-gray-700 text-xs py-1.5 px-2 rounded-lg hover:bg-gray-200"
                      >
                        💰 Getränke
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </>
          );
        })()}
      </div>

      {/* Modern employee history with Admin delete controls */}
      {showOrdersModal && selectedEmployeeForOrders && (
        <EmployeeHistoryModal
          employee={selectedEmployeeForOrders}
          onClose={() => {
            setShowOrdersModal(false);
            setSelectedEmployeeForOrders(null);
          }}
          currentDepartment={currentDepartment}
          apiBase={API}
          adminMode
          onOrderUpdate={onEmployeeUpdate}
        />
      )}
      
      {/* Balance Warning Modal */}
      {showBalanceWarning && (
        <BalanceWarningModal
          employeeName={balanceWarningData.employeeName}
          openBalances={balanceWarningData.openBalances}
          onClose={() => {
            setShowBalanceWarning(false);
            setBalanceWarningData({ employeeName: '', openBalances: [] });
            setPendingDeleteEmployee(null);
          }}
          onConfirmDelete={confirmDeleteEmployee}
        />
      )}
      
      {/* Floating Sticky Button for Bulk Payment (unten rechts) */}
      {selectedEmployees.size > 0 && (
        <button
          onClick={() => setShowBulkPaymentModal(true)}
          className="fixed bottom-6 right-6 z-40 bg-blue-600 text-white px-4 py-2.5 rounded-lg shadow-lg hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          {selectedEmployees.size} ausgewählt - Sammel-Buchung
        </button>
      )}
      
      {/* Success Notification */}
      {showSuccessNotification && (
        <SuccessNotification
          message={successMessage}
          onClose={() => {
            setShowSuccessNotification(false);
            setSuccessMessage('');
          }}
        />
      )}
      
      {/* Bulk Payment Modal (Sammel-Buchung) */}
      {showBulkPaymentModal && (
        <BulkPaymentModal
          selectedCount={selectedEmployees.size}
          departmentConfig={departmentConfig}
          onClose={() => setShowBulkPaymentModal(false)}
          onSubmit={handleBulkPayment}
        />
      )}

    </div>
  );
};

// Price Management Tab Component
const PriceManagementTab = ({ breakfastMenu, toppingsMenu, drinksMenu, sweetsMenu, onUpdatePrice, currentDepartment }) => {
  const rollTypeLabels = {
    'weiss': 'Helles Brötchen',
    'koerner': 'Körnerbrötchen'
  };

  const toppingLabels = {
    'ruehrei': 'Rührei',
    'spiegelei': 'Spiegelei',
    'eiersalat': 'Eiersalat',
    'salami': 'Salami',
    'schinken': 'Schinken',
    'kaese': 'Käse',
    'butter': 'Butter'
  };

  const updateItemPrice = (category, itemId, currentPrice) => {
    const newPrice = prompt('Neuer Preis (€):', currentPrice.toFixed(2));
    if (newPrice && !isNaN(parseFloat(newPrice))) {
      onUpdatePrice(category, itemId, newPrice);
    }
  };

  return (
    <div>
      <h3 className="text-lg font-semibold mb-6">Preise verwalten</h3>

      <div className="space-y-8">
        {/* Breakfast Items */}
        <div>
          <h4 className="text-md font-semibold mb-3 text-gray-700">Brötchen</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {breakfastMenu.map((item) => (
              <div key={item.id} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="font-medium">{rollTypeLabels[item.roll_type]}</span>
                  <button
                    onClick={() => updateItemPrice('breakfast', item.id, item.price)}
                    className="text-blue-600 hover:text-blue-800 font-semibold"
                  >
                    {item.price.toFixed(2)} €
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Toppings */}
        <div>
          <h4 className="text-md font-semibold mb-3 text-gray-700">Beläge</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {toppingsMenu.map((item) => (
              <div key={item.id} className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="font-medium">{toppingLabels[item.topping_type]}</span>
                  <button
                    onClick={() => updateItemPrice('toppings', item.id, item.price)}
                    className="text-green-600 hover:text-green-800 font-semibold"
                  >
                    {item.price.toFixed(2)} €
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Eier & Kaffee */}
        <CoffeeAndEggsManagement currentDepartment={currentDepartment} />

        {/* Drinks */}
        <div>
          <h4 className="text-md font-semibold mb-3 text-gray-700">Getränke</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {drinksMenu.map((item) => (
              <div key={item.id} className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="font-medium">{item.name}</span>
                  <button
                    onClick={() => updateItemPrice('drinks', item.id, item.price)}
                    className="text-purple-600 hover:text-purple-800 font-semibold"
                  >
                    {item.price.toFixed(2)} €
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sweets */}
        <div>
          <h4 className="text-md font-semibold mb-3 text-gray-700">Snacks</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sweetsMenu.map((item) => (
              <div key={item.id} className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="font-medium">{item.name}</span>
                  <button
                    onClick={() => updateItemPrice('sweets', item.id, item.price)}
                    className="text-orange-600 hover:text-orange-800 font-semibold"
                  >
                    {item.price.toFixed(2)} €
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Menu Management Tab Component  
const MenuManagementTab = ({ drinksMenu, sweetsMenu, onCreateMenuItem, onDeleteMenuItem, showNewDrink, setShowNewDrink, showNewSweet, setShowNewSweet }) => {
  return (
    <div>
      <h3 className="text-lg font-semibold mb-6">Menü verwalten</h3>

      <div className="space-y-8">
        {/* Drinks Management */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-md font-semibold text-gray-700">Getränke</h4>
            <button
              onClick={() => setShowNewDrink(true)}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
            >
              Neues Getränk
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {drinksMenu.map((item) => (
              <div key={item.id} className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="font-medium">{item.name}</span>
                    <p className="text-sm text-gray-600">{item.price.toFixed(2)} €</p>
                  </div>
                  <button
                    onClick={() => onDeleteMenuItem('drinks', item.id)}
                    className="text-red-600 hover:text-red-800 font-semibold"
                  >
                    Löschen
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sweets Management */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-md font-semibold text-gray-700">Snacks</h4>
            <button
              onClick={() => setShowNewSweet(true)}
              className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700"
            >
              Neuer Snack
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sweetsMenu.map((item) => (
              <div key={item.id} className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="font-medium">{item.name}</span>
                    <p className="text-sm text-gray-600">{item.price.toFixed(2)} €</p>
                  </div>
                  <button
                    onClick={() => onDeleteMenuItem('sweets', item.id)}
                    className="text-red-600 hover:text-red-800 font-semibold"
                  >
                    Löschen
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* New Item Modals */}
      {showNewDrink && (
        <NewMenuItemModal
          title="Neues Getränk hinzufügen"
          onCreateItem={(name, price) => onCreateMenuItem('drinks', name, price)}
          onClose={() => setShowNewDrink(false)}
        />
      )}

      {showNewSweet && (
        <NewMenuItemModal
          title="Neuen Snack hinzufügen"
          onCreateItem={(name, price) => onCreateMenuItem('sweets', name, price)}
          onClose={() => setShowNewSweet(false)}
        />
      )}

      {/* Success Notification */}
      {showSuccessNotification && (
        <SuccessNotification
          message={successMessage}
          onClose={() => {
            setShowSuccessNotification(false);
            setSuccessMessage('');
          }}
        />
      )}

      {/* Balance Warning Modal */}
      {showBalanceWarning && (
        <BalanceWarningModal
          employeeName={balanceWarningData.employeeName}
          openBalances={balanceWarningData.openBalances}
          onClose={() => {
            setShowBalanceWarning(false);
            setBalanceWarningData({ employeeName: '', openBalances: [] });
            setPendingDeleteEmployee(null);
          }}
          onConfirmDelete={confirmDeleteEmployee}
        />
      )}
    </div>
  );
};

// New Menu Item Modal
export const NewMenuItemModal = ({ title, onCreateItem, onClose }) => {
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !price || isNaN(parseFloat(price)) || isSubmitting) return;
    setIsSubmitting(true);
    const created = await onCreateItem(name.trim(), price);
    if (created !== false) {
      setName('');
      setPrice('');
    }
    setIsSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h2 className="text-xl font-bold mb-4">{title}</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Preis (€)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
              required
            />
          </div>
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:cursor-wait disabled:opacity-60"
            >
              {isSubmitting ? 'Wird erstellt …' : 'Erstellen'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600"
            >
              Abbrechen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// New Breakfast Item Modal
const NewBreakfastItemModal = ({ title, onCreateItem, onClose }) => {
  const [rollType, setRollType] = useState('');
  const [price, setPrice] = useState('');

  const rollTypeOptions = [
    { value: 'weiss', label: 'Helles Brötchen' },
    { value: 'koerner', label: 'Körnerbrötchen' }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (rollType && price && !isNaN(parseFloat(price))) {
      onCreateItem(rollType, price);
      setRollType('');
      setPrice('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h2 className="text-xl font-bold mb-4">{title}</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Brötchen-Typ</label>
            <select
              value={rollType}
              onChange={(e) => setRollType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
              required
            >
              <option value="">-- Brötchen-Typ wählen --</option>
              {rollTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Preis (€)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
              required
            />
          </div>
          <div className="flex gap-4">
            <button
              type="submit"
              className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
            >
              Erstellen
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600"
            >
              Abbrechen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// New Topping Item Modal
export const NewToppingItemModal = ({ title, onCreateItem, onClose }) => {
  const [toppingName, setToppingName] = useState('');
  const [price, setPrice] = useState('');
  const [purchaseEnabled, setPurchaseEnabled] = useState(false);
  const [purchaseGrams, setPurchaseGrams] = useState(50);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (toppingName.trim() && price && !isNaN(parseFloat(price))) {
      // Create a unique ID from the name
      const toppingId = toppingName.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
      const grams = purchaseEnabled ? purchaseGrams : 0;
      onCreateItem(toppingId, toppingName, price, grams);
      setToppingName('');
      setPrice('');
      setPurchaseEnabled(false);
      setPurchaseGrams(50);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h2 className="text-xl font-bold mb-4">{title}</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Belag-Name</label>
            <input
              type="text"
              value={toppingName}
              onChange={(e) => setToppingName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
              placeholder="z.B. Rührei, Käse, Salami..."
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Preis (€)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
              required
            />
          </div>
          <div className="mb-4">
            <label className="flex items-center gap-2 text-sm font-medium mb-2 cursor-pointer">
              <input
                type="checkbox"
                checked={purchaseEnabled}
                onChange={(e) => setPurchaseEnabled(e.target.checked)}
                className="rounded border-gray-300"
                data-testid="new-topping-purchase-toggle"
              />
              Einkaufsmenge berechnen
            </label>
            {purchaseEnabled && (
              <div className="flex items-center gap-2 mt-1 ml-6">
                <input
                  type="number"
                  min="1"
                  value={purchaseGrams}
                  onChange={(e) => setPurchaseGrams(parseInt(e.target.value) || 0)}
                  className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                  data-testid="new-topping-purchase-grams"
                />
                <span className="text-sm text-gray-500">g pro Hälfte</span>
              </div>
            )}
          </div>
          <div className="flex gap-4">
            <button
              type="submit"
              className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700"
            >
              Erstellen
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600"
            >
              Abbrechen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Bulk Payment Modal Component (Sammel-Buchung)
export const BulkPaymentModal = ({ selectedCount, departmentConfig, onClose, onSubmit }) => {
  const [amount, setAmount] = useState('');
  const [direction, setDirection] = useState(PAYMENT_DIRECTION_DEPOSIT);
  const [notes, setNotes] = useState('');
  const [bookedBy, setBookedBy] = useState('');
  const [balanceType, setBalanceType] = useState(departmentConfig.use_combined_accounts ? 'combined' : 'breakfast');
  
  const handleSubmit = (e) => {
    e.preventDefault();
    const parsedAmount = signedPaymentAmount(amount, direction);
    
    if (!amount || isNaN(parsedAmount)) {
      alert('Bitte geben Sie einen gültigen Betrag ein');
      return;
    }
    
    if (parsedAmount === 0) {
      alert('Betrag darf nicht 0 sein');
      return;
    }
    
    if (!bookedBy.trim()) {
      alert('Bitte geben Sie den Namen der buchenden Person ein.');
      return;
    }
    
    onSubmit({
      amount: parsedAmount,
      balance_type: balanceType,
      notes: notes.trim(),
      booked_by: bookedBy.trim(),
      payment_method: 'cash'
    });
    
    onClose();
  };
  
  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 py-8 overflow-y-auto"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg p-4 sm:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg sm:text-xl font-bold mb-4">💰 Sammel-Buchung</h2>
        <p className="text-xs sm:text-sm text-gray-600 mb-4">
          Für <strong>{selectedCount} Mitarbeiter</strong> den gleichen Betrag buchen
        </p>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Account Type Selection (nur bei getrennten Konten) */}
          {!departmentConfig.use_combined_accounts && (
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-2">
                Kontotyp auswählen
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setBalanceType('breakfast')}
                  className={`px-3 py-2 rounded-lg text-xs sm:text-sm border transition-colors ${
                    balanceType === 'breakfast'
                      ? 'bg-blue-100 border-blue-500 text-blue-700'
                      : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  🍽️ Essen
                </button>
                <button
                  type="button"
                  onClick={() => setBalanceType('drinks')}
                  className={`px-3 py-2 rounded-lg text-xs sm:text-sm border transition-colors ${
                    balanceType === 'drinks'
                      ? 'bg-purple-100 border-purple-500 text-purple-700'
                      : 'bg-gray-50 border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  🥤 Getränke/Snacks
                </button>
              </div>
            </div>
          )}
          
          {departmentConfig.use_combined_accounts && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-xs sm:text-sm text-green-700">
                <strong>Gemeinsames Konto:</strong> Essen, Getränke & Snacks
              </p>
            </div>
          )}

          <PaymentDirectionSelector value={direction} onChange={setDirection} />
          
          {/* Amount Input */}
          <div>
            <label className="block text-xs sm:text-sm font-medium mb-2">
              Betrag pro Mitarbeiter (€)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/-/g, ''))}
              placeholder="z.B. 10.00"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
              autoFocus
              data-testid="bulk-payment-amount"
            />
          </div>
          
          {/* Notes Input */}
          <div>
            <label className="block text-xs sm:text-sm font-medium mb-2">
              Notizen (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="z.B. Monatsabrechnung"
              rows="2"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
            />
          </div>
          
          {/* Gebucht von */}
          <div>
            <label className="block text-xs sm:text-sm font-medium mb-2">
              Gebucht von <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={bookedBy}
              onChange={(e) => setBookedBy(e.target.value)}
              placeholder="Ihr Name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
              required
              data-testid="bulk-payment-booked-by"
            />
          </div>
          
          {/* Summary */}
          {amount && !isNaN(signedPaymentAmount(amount, direction)) && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs sm:text-sm font-medium text-blue-700">
                Zusammenfassung:
              </p>
              <p className="text-xs sm:text-sm text-blue-600 mt-1">
                <strong>{selectedCount} Mitarbeiter</strong> erhalten je <strong>{signedPaymentAmount(amount, direction) >= 0 ? '+' : ''}{signedPaymentAmount(amount, direction).toFixed(2)}€</strong>
                {!departmentConfig.use_combined_accounts && ` auf ${balanceType === 'breakfast' ? 'Essen' : 'Getränke/Snacks'}`}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Gesamtbetrag: <strong>{(signedPaymentAmount(amount, direction) * selectedCount).toFixed(2)}€</strong>
              </p>
            </div>
          )}
          
          {/* Buttons */}
          <div className="flex gap-2 pt-2">
            <button
              type="submit"
              className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 text-sm sm:text-base font-medium"
            >
              Buchen
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600 text-sm sm:text-base"
            >
              Abbrechen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


// Help Tab Component - Renders HTML content from Developer Dashboard AND global Payment-Kantine push
const HelpTab = ({ globalHelpContent, globalHelpLastUpdated, onStartTutorial }) => {
  const hasContent = !!(globalHelpContent && globalHelpContent.trim());
  return (
    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm" data-testid="help-content">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-4">
        <h3 className="text-xl font-bold text-gray-800">Hilfe & Neues</h3>
        {globalHelpLastUpdated && (
          <span className="text-xs text-gray-500">
            zuletzt aktualisiert: {new Date(globalHelpLastUpdated).toLocaleString('de-DE')}
          </span>
        )}
      </div>
      <div className="mb-6 flex flex-col gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-sm font-semibold text-gray-900">Einführung erneut ansehen</div>
          <p className="mt-1 text-xs leading-5 text-gray-500">Funktionen kennenlernen und die lokale Bestellvorschau ausprobieren, ohne Einstellungen oder Salden zu ändern.</p>
        </div>
        <button type="button" onClick={onStartTutorial} className="shrink-0 rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700">
          Tutorial starten
        </button>
      </div>
      {hasContent ? (
        <div
          className="help-content"
          dangerouslySetInnerHTML={{ __html: globalHelpContent }}
        />
      ) : (
        <p className="text-gray-500 italic">Noch keine Hilfe-Inhalte vorhanden.</p>
      )}
    </div>
  );
};

// Referral Program Tab Component
export const ReferralProgramTab = ({ currentDepartment }) => {
  const [referralCode, setReferralCode] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    if (currentDepartment) {
      fetchReferralCode();
    }
  }, [currentDepartment]);

  const fetchReferralCode = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL || ''}/api/department-admin/referral-code/${currentDepartment.department_id}`
      );
      setReferralCode(response.data.referral_code);
    } catch (error) {
      console.error('Error fetching referral code:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (referralCode) {
      navigator.clipboard.writeText(referralCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="bg-gray-50 p-4 sm:p-6 rounded-lg border border-gray-200">
      <h3 className="text-lg font-semibold mb-5 text-gray-800">Werbeprogramm</h3>
      
      {/* Explanation Section */}
      <div className="mb-6 space-y-4">
        <div className="bg-white border border-gray-200 p-4 rounded-lg">
          <h4 className="font-semibold text-gray-900 mb-2">So funktioniert das Werbeprogramm</h4>
          <p className="text-gray-700">
            Wirb neue Wachabteilungen deiner eigenen oder einer anderen Wache. Deine und die geworbene Wachabteilung erhalten jeweils Gratismonate!
          </p>
          <p className="text-gray-700 font-medium mt-2">
            Voraussetzung: Die geworbene Wachabteilung muss insgesamt mindestens 12 Monate gebucht haben – einmal 12 Monate oder zweimal 6 Monate.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white border border-gray-200 p-4 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-9 h-9 bg-gray-700 rounded-full flex items-center justify-center text-white font-semibold">
                  1
                </div>
              </div>
              <div className="ml-4">
                <h5 className="font-semibold text-gray-900 mb-1">Bisher nicht teilnehmende Wache</h5>
                <p className="text-gray-600 text-sm">
                  Du wirbst die erste Wachabteilung einer Wache, von der bisher noch keine Wachabteilung gebucht hat:
                </p>
                <p className="text-gray-900 font-semibold mt-2">3 Gratismonate für deine Wachabteilung</p>
                <p className="text-gray-600 text-sm mt-1">+ 1 Gratismonat für die geworbene Wachabteilung</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 p-4 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-9 h-9 bg-gray-700 rounded-full flex items-center justify-center text-white font-semibold">
                  2
                </div>
              </div>
              <div className="ml-4">
                <h5 className="font-semibold text-gray-900 mb-1">Weitere Abteilung einer teilnehmenden Wache</h5>
                <p className="text-gray-600 text-sm">
                  Hat bereits eine Wachabteilung dieser Wache gebucht, gilt für jede weitere geworbene
                  Abteilung – auch an deiner eigenen Wache und unabhängig davon, wer sie wirbt:
                </p>
                <p className="text-gray-900 font-semibold mt-2">1 Gratismonat für deine Wachabteilung</p>
                <p className="text-gray-600 text-sm mt-1">+ 1 Gratismonat für die geworbene Wachabteilung</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 p-4 rounded-lg">
          <p className="text-gray-700 mb-4">
            Der 3-Monats-Bonus gilt nur einmal pro bisher nicht teilnehmender Wache, nicht pro Werbendem.
            Wirbst du deren erste und anschließend drei weitere Wachabteilungen, erhältst du insgesamt
            3 + 1 + 1 + 1 = 6 Gratismonate. Jede geworbene Wachabteilung erhält 1 Gratismonat.
            Du verwendest dafür immer denselben Werbecode.
          </p>
          <h4 className="font-semibold text-gray-900 mb-2">So gehst du vor</h4>
          <ol className="list-decimal list-inside space-y-2 text-gray-700">
            <li>Teile deinen Werbecode mit Kollegen deiner eigenen oder anderer Wachen</li>
            <li>Jede geworbene Wachabteilung teilt mir nach der Registrierung den verwendeten Code mit</li>
            <li>Ich ordne die geworbene Wachabteilung zu und aktiviere die Gratismonate für beide Parteien</li>
          </ol>
        </div>
      </div>

      {/* Referral Code Display */}
      <div className="bg-white border border-gray-300 p-5 rounded-lg">
        <h4 className="text-base font-semibold text-gray-900 mb-2">Dein persönlicher Werbecode</h4>
        <p className="text-gray-600 text-sm mb-4">
          Dieser Code ist einmalig für deine Wachabteilung und kann nicht geändert werden.
        </p>
        
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-500"></div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex-1">
                <div className="text-2xl sm:text-3xl text-gray-900 font-mono font-bold tracking-wider break-all">
                  {referralCode || 'Wird geladen...'}
                </div>
                <div className="text-gray-500 text-sm mt-1">
                  {currentDepartment.department_name}
                </div>
              </div>
              <button
                onClick={copyToClipboard}
                className="w-full sm:w-auto bg-gray-800 text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-900 transition-colors flex items-center justify-center space-x-2"
              >
                {copied ? (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Kopiert!</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span>Kopieren</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Contact Info */}
      <div className="mt-6 bg-gray-50 border border-gray-200 p-4 rounded-lg">
        <h4 className="font-bold text-gray-900 mb-2">📧 Ablauf der Zuordnung:</h4>
        <p className="text-gray-700 text-sm">
          Sobald eine neue Wachabteilung deinen Code bei der Registrierung verwendet und mir den Code mitteilt,
          ordne ich sie deiner Wachabteilung zu. Die Verlängerung für beide Parteien wird aktiviert,
          sobald die Buchungsvoraussetzung erfüllt ist.
        </p>
        <p className="text-gray-700 text-sm mt-2">
          <strong>Wichtig:</strong> Die Gratismonate für beide Wachabteilungen werden nur gutgeschrieben,
          sobald die geworbene Wachabteilung insgesamt mindestens 12 Monate gebucht hat.
          Das ist mit einer Buchung über 12 Monate oder zwei Buchungen über jeweils 6 Monate möglich.
          Bei zweimal 6 Monaten erfolgt die Gutschrift erst nach der zweiten Buchung.
        </p>
      </div>
    </div>
  );
};


export default DepartmentAdminDashboard;
